import React, { useEffect, useRef, useState } from 'react';
import CourseInfoBox from '../CourseInfoBox';
import CurriculumFileViewer from './CurriculumFileViewer';
import 'firebase/firestore';
import CurriculumFileUploader from './CurriculumFileUploader';
import { FileInfo } from '@data/course/courseData';
import { validateCurriculum } from '@data/course/validateCurriculum';

interface CurriculumInfoBoxProps {
  courseFrozen: boolean;
  initialFiles: FileInfo[];
  onChange: (files: FileInfo[]) => Promise<void>;
  updateValidity: (valid: boolean) => void;
}

const CurriculumInfoBox: React.FC<CurriculumInfoBoxProps> = ({
  courseFrozen,
  initialFiles,
  updateValidity,
  onChange,
}) => {
  const [files, setFiles] = useState(initialFiles);
  const [error, setError] = useState('');

  const didMount = useRef(false);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    if (courseFrozen) {
      return;
    }

    updateValidity(validateCurriculum(files));
    onChange(files)
      .then(() => {
        setError('');
      })
      .catch((e: Error) => setError(e.message));
  }, [files, updateValidity, onChange, courseFrozen]);

  return (
    <CourseInfoBox
      className='col-span-3 lg:col-span-1'
      header='Curriculum'
      description={
        courseFrozen
          ? 'You have submitted your curriculum.'
          : 'Submit curriculum below. Use the confirm button in the "Course Info" section to make your final submission.'
      }
    >
      <div className='flex flex-col'>
        {courseFrozen ? (
          <CurriculumFileViewer files={initialFiles} />
        ) : (
          <CurriculumFileUploader
            files={files}
            addFiles={(newFiles) => setFiles((files) => files.concat(newFiles))}
            removeFile={(f) =>
              setFiles((files) => {
                const filesCopy = [...files];
                const index = filesCopy.indexOf(f);
                filesCopy.splice(index, 1);
                return filesCopy;
              })
            }
          />
        )}
        <p className='text-red-600'>{error}</p>
      </div>
    </CourseInfoBox>
  );
};

export default CurriculumInfoBox;
